import cnBind from 'classnames/bind';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { GxButton, GxIcon, GxInput } from '@garpix/garpix-web-components-react';
import { useMediaQuery } from 'react-responsive';
import { useStoreon } from 'storeon/react';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { api } from '@/api';
import { AgGridReact } from 'ag-grid-react/lib/';
import { State, Events } from '@/interfaces';
import { IPreset, IPresetFilter } from '@/types';
import { months, weekDays } from '../../../const/calendar';
import { defaultValuesColumnOrders, defaultValuesColumnPayments, STATISTICS_TAB } from '../const';
import styles from './styles/index.module.scss';
import CalendarInput from '../../../components/CalendarInput';
import Button from '../../../components/Button';
import { PrinterIcon } from '../../../assets';
import Burger from '../../../components/Burger';


interface IStatisticsHeader {
    tab: STATISTICS_TAB,
    setTab: (name: STATISTICS_TAB) => void,
    handleClickDatePicker: (e: any) => void,
    defaultDate?: DateObject[],
    gridRef: React.RefObject<AgGridReact>
}

const StatisticsHeader: FC<IStatisticsHeader> = ({ tab, setTab, handleClickDatePicker, defaultDate = [], gridRef }) => {
    const [dateInput, setDateInput] = useState<DateObject[]>([]);
    const [dateValue, setDateValue] = useState<DateObject[]>([]);
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false)
    const [presetName, setPresetName] = useState<string>('');
    const [changeItem, setChangeItem] = useState<number | null>(null);
    const { dispatch, presets } = useStoreon<State, Events>('presets')

    const isDesktopOrMobile = useMediaQuery({
        query: '(max-width: 568px)'
    })
    const cn = cnBind.bind(styles);
  useEffect(() => {
    dispatch('presets/get', { report_name: tab });
  }, [])

  const getPresets = () => {
    if (gridRef?.current === null) {
      return {
        groups_key: [],
        filters: {},
        values: [],
        pivot: false,
        visibles: [],
        moves: []
      }
    }
    // пресет группировок, gridOptions.columnApi.setRowGroupColumns(colKeys)
    const groups_key = gridRef.current.columnApi.getRowGroupColumns().map((element) => {
      return element.getColId()
    });

    // фильтры, gridOptions.api.setFilterModel(model)
    const filters: Record<string, IPresetFilter> = gridRef.current.api.getFilterModel();

    // получаем состояние текущих колонок
    const visibles = gridRef.current.columnApi.getColumnState();

    // порядок, gridOptions.columnApi.moveColumn('id', 1)
    const moves = gridRef.current.columnApi.getAllGridColumns().sort((a, b) => {

      const ALeft = a.getLeft();
      const BLeft = b.getLeft();

      if (ALeft === null || BLeft === null) {
        return 0;
      }

      if (ALeft > BLeft) {
        return 1;
      }

      if (ALeft < BLeft) {
        return -1;
      }
      return 0;
    }).map((element, index) => {
      return {
        colKey: element.getColId(),
        toIndex: index
      }
    });

    // суммы gridOptions.columnApi.getValueColumns()
    const values = gridRef.current.columnApi.getValueColumns().map((element) => {
      return element.getColId()
    });
    const pivot = gridRef.current.columnApi.isPivotMode();

    return {
      'groups_key': groups_key,
      'filters': filters,
      'visibles': visibles,
      'moves': moves,
      'values': values,
      'pivot': pivot
    }
  }
  
    const addPreset = useCallback(async (name: string, tab: STATISTICS_TAB) => {

        const data = {
            name,
            data: getPresets(),
            report_name: tab
        }
        try {
            await api.reports.addPresets( data );
            dispatch('presets/get', { report_name: tab });
        } catch (e) {
            dispatch('presets/get', { report_name: tab });
        }
        setPresetName('')
    }, []);

  const setPreset = (id: number, presets: IPreset[]) => {
    const preset = presets.find((item) => item.id === id)
    if (preset && gridRef.current) {
      setChangeItem(preset.id);
      setPresetName(preset.name);

      preset.data.moves?.forEach((element) => {
        setTimeout(() => {
          gridRef.current?.columnApi.moveColumn(element.colKey, element.toIndex);
        }, 500);
      });

      gridRef.current.columnApi.applyColumnState({
        state: preset.data.visibles,
        applyOrder: true,
      });

      gridRef.current.api.setFilterModel(preset.data.filters);
      gridRef.current.columnApi.setRowGroupColumns(preset.data.groups_key);
      gridRef.current.columnApi.addValueColumns(preset.data.values);
      gridRef.current.columnApi.setPivotMode(preset.data.pivot);

    }
  };

    const clearPreset = async (id?: number) => {
        setChangeItem(null);
        setPresetName('');
        const arr = tab === STATISTICS_TAB.ORDERS 
            ? defaultValuesColumnOrders.map((item) => item.field ? item.field : '')
            : defaultValuesColumnPayments.map((item) => item.field ? item.field : '')

        gridRef?.current?.columnApi.setColumnsVisible(arr, true);

        try {
            if (id) {
                await api.reports.deletePresets(id);
                dispatch('presets/get', { report_name: tab });
            }
        } catch (e) {
            dispatch('presets/get', { report_name: tab });
        }
    }

    const changePreset = async (id: number) => {
        try {
            await api.reports.changePresets(id, {
                name: presetName,
                data: getPresets()
            });
            dispatch('presets/get', { report_name: tab });
        } catch (e) {
            dispatch('presets/get', { report_name: tab });
        }
    }

    useEffect(() => {
        if (defaultDate.length !== 0) {
            setDateValue(defaultDate);
            setSubmitEnabled(true)
        } else {
            setSubmitEnabled(false)
        }
    }, [defaultDate]);

    useEffect(() => {
        clearPreset();
    }, [tab])


    return (
        <div>
            <div className={styles['header']}>
                <div className={styles['header--first-sec']}>
                    <div className={styles['header--title']}>               
                      <Burger />
                      Отчеты
                    </div>
                    <div className={styles['tabs--menu']}>
                        <div
                            tabIndex={0} role='button'
                            onClick={() => setTab(STATISTICS_TAB.ORDERS)}
                            className={cn(styles['tabs--item'], {
                                [styles['tabs--item_active']]: tab === STATISTICS_TAB.ORDERS
                            })}
                        >
                            Заявки
                        </div>
                        <div tabIndex={0} role='button'
                             onClick={() => setTab(STATISTICS_TAB.PAYMENTS)}
                             className={cn(styles['tabs--item'], {
                                 [styles['tabs--item_active']]: tab === STATISTICS_TAB.PAYMENTS
                             })}
                        >
                            Платежи
                        </div>
                        <div tabIndex={0} role='button'
                             onClick={() => setTab(STATISTICS_TAB.INVOICES)}
                             className={cn(styles['tabs--item'], {
                                 [styles['tabs--item_active']]: tab === STATISTICS_TAB.INVOICES
                             })}
                        >
                            Счета
                        </div>
                    </div>
                </div>
                <div className={styles['header--second-sec']}>
                    <div className={styles['header--datepicker']}>
                        <span className={styles['select--title']}>Диапазон отчета:</span>
                        <DatePicker
                            format='DD.MM.YYYY'
                            weekDays={weekDays}
                            months={months}
                            value={dateValue}
                            onOpen={() => { setSubmitEnabled(false) }}
                            onChange={date => {
                                if (date && date instanceof Array) {
                                    if (date.length === 2) {
                                        setDateInput([date[0], date[1]])
                                    } else {
                                        setDateInput([date[0], date[0]])
                                    }
                                } else {
                                    setDateInput([])
                                }
                            }}
                            onClose={() => { 
                                setDateValue(dateInput)
                                setSubmitEnabled(true)
                            }}
                            weekStartDayIndex={1}
                            render={<CalendarInput
                                type='md_radius'
                            />
                            }
                            range
                        />
                    </div>
                    <Button theme='btn--outline_blue_radius' className={styles['btn']} disable={!submitEnabled} onClick={() => {
                        handleClickDatePicker(dateValue);
                        setChangeItem(null);
                        setPresetName('')
                    }}>
                        {isDesktopOrMobile ? <GxIcon src={PrinterIcon} className={styles['icon']} /> : 'Сформировать отчет'}
                    </Button>
                </div>
            </div>
            <div className={styles['presets']}>
                <div className={styles['presets__list']}>
                    {presets.map((item) =>
                        <div className={cn({
                            'presets__item': true,
                            'presets__item--active': changeItem === item.id
                        })} key={item.id}>
                            <span className={styles['presets__item--text']}
                                  role='button'
                                  tabIndex={0}
                                  onClick={() => {
                                      if (changeItem !== item.id) setPreset(item.id, presets)
                                  }}>
                                {item.name}
                            </span>
                            <span
                                className={styles['presets__item--icon']}
                                onClick={() => clearPreset(item.id)}
                                role='button'
                                tabIndex={0}
                            />
                        </div>
                    )}
                </div>
                <div className={styles['presets-block']} >
                    <GxInput
                        className={styles['presets--input']}
                        value={presetName}
                        onGx-change={(e: Record<string, any>) => setPresetName(e.target.value)}
                        clearable={!!changeItem}
                        onGx-clear={() => clearPreset()}
                    />
                    <GxButton className={styles['presets--btn']} onClick={() => {
                        if (changeItem) {
                            changePreset(changeItem)
                        } else {
                            addPreset(presetName, tab)
                        }
                    }}>{changeItem ? 'Изменить пресет' : 'Сохранить как новый пресет'}</GxButton>
                </div>
            </div>
        </div>
    )
}

export default StatisticsHeader;
